import { useCallback, useEffect, useMemo } from "react";
import { CssBaseline } from "@mui/material";
import ThemeProvider from "./theme/ThemeProvider";
import {
  InteractionRequiredAuthError,
  InteractionStatus,
  InteractionType,
} from "@azure/msal-browser";
import { MsalAuthenticationTemplate, useMsal } from "@azure/msal-react";
import { Navigate, useRoutes } from "react-router-dom";
import { loginRequest } from "authConfig";
import SidebarLayout from "layouts/SidebarLayout";
import AppLoader from "shared-compoents/app-loader";
import AuthError from "shared-compoents/auth-error";
import { SidebarProvider } from "contexts/SidebarContext";
import ExploreTopics from "pages/explore-topics";
import TopicDetail from "pages/topic-detail";
import Documentation from "pages/documentation";
import ComingSoon from "pages/coming-soon";
import { useAppDispatch, useAppSelector } from "store";
import { setUserData } from "store/userDataSlice";

const routes = [
  {
    path: "/",
    element: <SidebarLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/explore-topics" replace />,
      },
      {
        path: "explore-topics",
        element: <ExploreTopics />,
      },
      {
        path: "explore-topics/:topicId",
        element: <TopicDetail />,
      },
      {
        path: "documentation/:pageId",
        element: <Documentation />,
      },
      {
        path: "self-service/portal",
        element: <ComingSoon title="Self Service Portal" />,
      },
      {
        path: "self-service/track-requests",
        element: <ComingSoon title="Track Requests" />,
      },
      {
        path: "*",
        element: <Navigate to="/explore-topics" replace />,
      },
    ],
  },
];

// Rendered only once the user is authenticated. Acquires the API token and
// renders the routed application.
function AuthenticatedApp() {
  const { instance, accounts, inProgress } = useMsal();
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((state) => state.userData.access_token);

  useEffect(() => {
    if (accounts[0] && !instance.getActiveAccount()) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [accounts, instance]);

  const acquireToken = useCallback(async () => {
    const account = instance.getActiveAccount() || accounts[0];
    if (!account) return;
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      dispatch(setUserData({
        user_name: response.account?.username ?? '',
        name: response.account?.name ?? '',
        access_token: response.accessToken,
      }));
    } catch (error) {
      // Only fall back to an interactive redirect when no other interaction is
      // in progress, to avoid the interaction_in_progress error / redirect loop.
      if (error instanceof InteractionRequiredAuthError && inProgress === InteractionStatus.None) {
        instance.acquireTokenRedirect(loginRequest).catch(() => {
          /* Token redirect errors are surfaced via MSAL's event callbacks */
        });
      }
    }
  }, [accounts, dispatch, instance, inProgress]);

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      acquireToken();
    }
  }, [inProgress, acquireToken]);

  const content = useRoutes(routes);

  if (!accessToken) {
    return <AppLoader />;
  }

  return <SidebarProvider>{content}</SidebarProvider>;
}

function App() {
  const authRequest = useMemo(() => ({ scopes: loginRequest.scopes }), []);

  return (
    <ThemeProvider>
      <CssBaseline />
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={authRequest}
        loadingComponent={AppLoader}
        errorComponent={AuthError}
      >
        <AuthenticatedApp />
      </MsalAuthenticationTemplate>
    </ThemeProvider>
  );
}

export default App;
